import {
  createLimitOrder,
  createLimitStopOrder,
  createMarketOrder,
} from '#/api/apiExchange';
import { ApiOrder } from '#/api/types';
import { prepareBothConditionalTPSLOrders } from '#/components/positions/takeProfitStopLoss/tpSlUtils';
import { signTypedDataWithModal } from '#/lib/ethereum/sign/signHashWithModal';
import { TypedDataSignParams } from '#/lib/ethereum/sign/typedData/eip712';
import { UnreachableCaseError } from '#/lib/utils';
import { DelegatedKeyAuthorization } from '#/reduxState/delegatedKey/authorizationReducer';
import { IWallet } from '#/reduxState/wallet/walletReducer';

import { cancelOrderWithSignModal } from './orderCancelUtils';

/**
 * Note that Stop orders could lead to instant execution if set price incorrectly.
 *  Always compare current index/last price before calling.
 */
export async function cancelAndRecreateOrder({
  order,
  updatedPrice,
  wallet,
  delegatedKeyAuthorization,
}: {
  order: ApiOrder;
  updatedPrice: number;
  wallet: IWallet;
  delegatedKeyAuthorization: DelegatedKeyAuthorization;
}) {
  console.log('[repriceOrder] Canceling existing order');
  await cancelOrderWithSignModal(
    order.orderId,
    wallet,
    delegatedKeyAuthorization
  );
  console.log('[chart] Placing order at updated price');
  const signTypedData = (...typedData: TypedDataSignParams) =>
    signTypedDataWithModal(
      wallet,
      delegatedKeyAuthorization,
      typedData,
      'Please sign the order on your device.'
    );

  const updatedPriceFormatted = Number(updatedPrice).toFixed(8);

  const coreParams = {
    baseSymbol: order.baseSymbol,
    quoteSymbol: order.quoteSymbol,
    delegatedKeyWallet: delegatedKeyAuthorization.walletAddress || undefined,
    quantity: order.originalQuantity,
    reduceOnly: order.reduceOnly,
    side: order.side,
    signTypedData,
    walletAddress: wallet.walletAddress,
    timeInForce: order.timeInForce,
  } as const;

  if (!order.type) {
    return;
  }

  const conditionalQuantity =
    order.conditionalStopLossOrder?.originalQuantity ||
    order.conditionalTakeProfitOrder?.originalQuantity;

  const tpSlParams = conditionalQuantity
    ? await prepareBothConditionalTPSLOrders({
        delegatedWallet: delegatedKeyAuthorization,
        market: order.market,
        parentOrderSide: order.side,
        maximumPositionSize: conditionalQuantity,
        stopLossPrice: order.conditionalStopLossOrder?.triggerPrice,
        stopLossPriceType: order.conditionalStopLossOrder?.triggerType,
        takeProfitPrice: order.conditionalTakeProfitOrder?.triggerPrice,
        takeProfitPriceType: order.conditionalTakeProfitOrder?.triggerType,
        wallet,
      })
    : undefined;

  switch (order.type) {
    case 'limit':
      await createLimitOrder(
        {
          type: order.type,
          ...coreParams,
          price: updatedPriceFormatted,
          // timeInForce: 'gtx', // We display Instant exec warning instead
        },
        tpSlParams
      );
      break;
    case 'takeProfitLimit':
    case 'stopLossLimit': {
      if (!order.price || !order.triggerType) {
        throw new Error('Unknown price or trigger');
      }
      // Warn: This could lead to instant execution. Check before calling function
      await createLimitStopOrder(
        {
          type: order.type,
          ...coreParams,
          price: order.price,
          triggerPrice: updatedPriceFormatted,
          triggerType: order.triggerType,
          // timeInForce: 'gtx',
        },
        tpSlParams
      );
      break;
    }
    case 'takeProfitMarket':
    case 'stopLossMarket': {
      if (!order.triggerPrice || !order.triggerType) {
        throw new Error('Unknown price or trigger');
      }
      await createMarketOrder(
        {
          type: order.type,
          ...coreParams,
          triggerPrice: updatedPriceFormatted,
          triggerType: order.triggerType,
        },
        tpSlParams
      );
      break;
    }
    case 'market':
    case 'trailingStopMarket':
      throw new Error('Unsupported order type');
    default:
      throw new UnreachableCaseError(order.type);
  }
}
